import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import { Stack, Typography, MenuItem, IconButton, Tooltip, Grid, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import LoadingButton from '@mui/lab/LoadingButton';
import CustomDatePicker from 'src/components/custom-date-picker';
import CustomPauseUnpauseDateSelect from 'src/components/custom-pause-unpause-date-select';

import Iconify from 'src/components/iconify';
import { TablePaginationCustom } from 'src/components/table';
import { useGetRestaurantSubscriptions, useGetCurrentDateTime } from 'src/api/restaurant';
import { useGetRestaurantFilterOptions } from 'src/api/filter-options';
import { components } from 'src/types/schema';
import Label from 'src/components/label';
import { useAuthContext } from 'src/auth/hooks';
import { hasPermissions } from 'src/auth/guard/utils';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { client } from 'src/api/client';
import { useSnackbar } from 'src/components/snackbar';

// ----------------------------------------------------------------------

interface ISubscriptionTableFilters {
  title: string;
  status: "active" | "cancelled" | "old" | "unpaid" | "all";
  created_at_gte: string | null;
  created_at_lte: string | null;
}

type ISubscriptionTableFilterValue = string | null;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'old', label: 'Old' },
];

// ----------------------------------------------------------------------

type SubscriptionTableToolbarProps = {
  filters: ISubscriptionTableFilters;
  onFilters: (name: string, value: ISubscriptionTableFilterValue) => void;
  onResetFilters: () => void;
};

function SubscriptionTableToolbar({ filters, onFilters, onResetFilters }: SubscriptionTableToolbarProps) {
  const handleFilterName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilters('title', event.target.value);
    },
    [onFilters]
  );

  const handleFilterStatus = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilters('status', event.target.value);
    },
    [onFilters]
  );

  const handleFilterStartDate = useCallback(
    (newValue: Date | null) => {
      // Format date in local timezone as YYYY-MM-DD to avoid UTC offset
      onFilters(
        'created_at_gte',
        newValue ? newValue.toLocaleDateString('en-CA') : null
      );
    },
    [onFilters]
  );

  const handleFilterEndDate = useCallback(
    (newValue: Date | null) => {
      // Format date in local timezone as YYYY-MM-DD to avoid UTC offset
      onFilters(
        'created_at_lte',
        newValue ? newValue.toLocaleDateString('en-CA') : null
      );
    },
    [onFilters]
  );

  return (
    <Stack
      spacing={2}
      alignItems={{ xs: 'flex-end', md: 'center' }}
      direction={{
        xs: 'column',
        md: 'row',
      }}
      sx={{
        p: 2.5,
        pr: { xs: 2.5, md: 1 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1 }}>
        <TextField
          fullWidth
          value={filters.title}
          onChange={handleFilterName}
          placeholder="Search..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          select
          label="Status"
          value={filters.status}
          onChange={handleFilterStatus}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: { maxHeight: 220 },
              },
            },
          }}
          sx={{ maxWidth: { md: 160 } }}
        >
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <CustomDatePicker
          label="From"
          value={filters.created_at_gte ? new Date(filters.created_at_gte) : null}
          onChange={handleFilterStartDate}
          format="dd/MM/yyyy"
          slotProps={{
            textField: {
              fullWidth: true,
              sx: { maxWidth: { md: 160 } },
            },
          }}
        />

        <CustomDatePicker
          label="To"
          value={filters.created_at_lte ? new Date(filters.created_at_lte) : null}
          onChange={handleFilterEndDate}
          format="dd/MM/yyyy"
          slotProps={{
            textField: {
              fullWidth: true,
              sx: { maxWidth: { md: 160 } },
            },
          }}
        />

        <Tooltip title="Reset filters">
          <IconButton onClick={onResetFilters}>
            <Iconify icon="mdi:filter-off-outline" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

// ----------------------------------------------------------------------

function useSubscriptionFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = {
    title: searchParams.get('title') || '',
    status: (searchParams.get('status') as ISubscriptionTableFilters['status']) || 'all',
    created_at_gte: searchParams.get('created_at_gte'),
    created_at_lte: searchParams.get('created_at_lte'),
  };

  const onFilters = useCallback((name: string, value: ISubscriptionTableFilterValue) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set(name, value);
    } else {
      params.delete(name);
    }
    params.delete('page'); // Reset page when filters change
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const resetFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    // Only remove filter-related parameters
    params.delete('title');
    params.delete('status');
    params.delete('created_at_gte');
    params.delete('created_at_lte');
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  return { filters, onFilters, resetFilters };
}

function useSubscriptionPagination() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 0;
  const rowsPerPage = Number(searchParams.get('rowsPerPage')) || 25;

  const onPageChange = useCallback((newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const onRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('rowsPerPage', String(newRowsPerPage));
    params.delete('page'); // Reset to first page when changing rows per page
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  return { page, rowsPerPage, onPageChange, onRowsPerPageChange };
}

// ----------------------------------------------------------------------

type BulkOperationsModalProps = {
  open: boolean;
  onClose: () => void;
  restaurantId?: string;
  onSuccess: () => void;
};

function BulkOperationsModal({ open, onClose, restaurantId, onSuccess }: BulkOperationsModalProps) {
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const { currentDateTime } = useGetCurrentDateTime();
  const { restaurantFilterOptions } = useGetRestaurantFilterOptions();
  
  const [operationType, setOperationType] = useState<'pause' | 'unpause'>('pause');
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');
  const [pauseStartDate, setPauseStartDate] = useState<string>('');
  const [pauseEndDate, setPauseEndDate] = useState<string>('');
  const [unpauseFromDate, setUnpauseFromDate] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const canHandleMultipleRestaurants = hasPermissions(user, ['partners:misc:handle_multiple_restaurants']);
  const shouldShowRestaurantSelector = canHandleMultipleRestaurants || (user?.restaurants && user.restaurants.length > 1);

  // Get restaurant options
  const restaurantOptions = canHandleMultipleRestaurants 
    ? restaurantFilterOptions 
    : (user?.restaurants || []);

  // Get default restaurant ID
  const getDefaultRestaurantId = () => {
    if (restaurantId) return restaurantId;
    if (user?.restaurants && user.restaurants.length > 0) {
      return user.restaurants[0].id.toString();
    }
    return '';
  };

  // Initialize selected restaurant
  useEffect(() => {
    if (!selectedRestaurantId) {
      setSelectedRestaurantId(getDefaultRestaurantId());
    }
  }, [selectedRestaurantId, restaurantId, user?.restaurants]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setOperationType('pause');
      setPauseStartDate('');
      setPauseEndDate('');
      setUnpauseFromDate('');
      setSelectedRestaurantId(getDefaultRestaurantId());
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedRestaurantId) {
      enqueueSnackbar('Please select a restaurant', { variant: 'error' });
      return;
    }

    if (operationType === 'pause' && (!pauseStartDate || !pauseEndDate)) {
      enqueueSnackbar('Please select both start and end dates', { variant: 'error' });
      return;
    }

    if (operationType === 'unpause' && !unpauseFromDate) {
      enqueueSnackbar('Please select from date', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);

      if (operationType === 'pause') {
        await client.POST('/partners/restaurant-subscriptions/{id}/bulk-pause/', {
          params: {
            path: { id: Number(selectedRestaurantId) }
          },
          body: {
            start_date: pauseStartDate,
            end_date: pauseEndDate,
          }
        });
      } else {
        await client.POST('/partners/restaurant-subscriptions/{id}/bulk-unpause/', {
          params: {
            path: { id: Number(selectedRestaurantId) }
          },
          body: {
            from_date: unpauseFromDate,
            override_client: true,
          }
        });
      }

      enqueueSnackbar(
        `Bulk ${operationType} operation was successful`,
        { variant: 'success' }
      );
      
      onClose();
      onSuccess()

    } catch (error) {
      console.error(`Bulk ${operationType} failed:`, error);
      enqueueSnackbar(`Bulk ${operationType} operation failed`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={3}>
          <Iconify icon="mdi:pause" />
          <Typography variant="h6">Bulk Pause/Unpause Feature</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 0 }}>
         

          {/* Restaurant Selection */}
          {shouldShowRestaurantSelector && (
            <FormControl fullWidth>
              <TextField
                select
                label="Restaurant"
                value={selectedRestaurantId}
                onChange={(e) => setSelectedRestaurantId(e.target.value)}
                required
                sx={{
                  mt: 3
                }}
              >
                {restaurantOptions.map((restaurant) => (
                  <MenuItem key={restaurant.id} value={restaurant.id.toString()}>
                    {restaurant.name_en}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          )}

          {/* Operation Type Selection */}
          <FormControl component="fieldset">
            <RadioGroup
              value={operationType}
              onChange={(e) => setOperationType(e.target.value as 'pause' | 'unpause')}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: operationType === 'pause' ? 2 : 1,
                      borderColor: operationType === 'pause' ? 'warning.main' : 'divider',
                      '&:hover': {
                        borderColor: 'warning.main',
                      },
                    }}
                    onClick={() => setOperationType('pause')}
                  >
                    <FormControlLabel
                      value="pause"
                      control={<Radio />}
                      label={
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle1">Proceed to Pause</Typography>
                          </Stack>
              
                        </Stack>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: operationType === 'unpause' ? 2 : 1,
                      borderColor: operationType === 'unpause' ? 'success.main' : 'divider',
                      '&:hover': {
                        borderColor: 'success.main',
                      },
                    }}
                    onClick={() => setOperationType('unpause')}
                  >
                    <FormControlLabel
                      value="unpause"
                      control={<Radio />}
                      label={
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                           
                            <Typography variant="subtitle1">Proceed to Unpause</Typography>
                          </Stack>
                        
                        </Stack>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Card>
                </Grid>
              </Grid>
            </RadioGroup>
          </FormControl>

          {/* Date Selection */}
          {operationType === 'pause' ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <CustomPauseUnpauseDateSelect
                  label="Start Date"
                  value={pauseStartDate}
                  onChange={(dateStr) => {
                    setPauseStartDate(dateStr);
                    // Clear end date when start date changes to force user to reselect
                    if (pauseEndDate && dateStr && new Date(dateStr) > new Date(pauseEndDate)) {
                      setPauseEndDate('');
                    }
                  }}
                  required
                  serverDate={currentDateTime?.date}
                  offDays={[]} // No off days for bulk operations
                  maxDays={30}
                  defaultDaysOffset={0}
                  showLockedChips
                  showOffDayChips={false}
                  minDate={currentDateTime?.date}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomPauseUnpauseDateSelect
                  label="End Date"
                  value={pauseEndDate}
                  onChange={setPauseEndDate}
                  required
                  disabled={!pauseStartDate}
                  startDate={pauseStartDate}
                  serverDate={currentDateTime?.date}
                  offDays={[]} // No off days for bulk operations
                  maxDays={30}
                  defaultDaysOffset={-1} // Don't auto-select end date
                  showLockedChips
                  showOffDayChips={false}
                />
              </Grid>
            </Grid>
          ) : (
            <CustomPauseUnpauseDateSelect
              label="From Date"
              value={unpauseFromDate}
              onChange={setUnpauseFromDate}
              required
              serverDate={currentDateTime?.date}
              offDays={[]} // No off days for bulk operations
              maxDays={30}
              defaultDaysOffset={0}
              showLockedChips
              showOffDayChips={false}
              minDate={currentDateTime?.date}
            />
          )}

          <Box sx={{ 
            p: 2, 
            bgcolor: 'main', 
            borderRadius: 1, 
            border: '1px solid', 
            borderColor: operationType === 'pause' ? 'warning.main' : 'success.main' 
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600, 
                mb: 1 
              }}
            >
              What happens next?
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              {operationType === 'pause' ? (
                <>
                  <li>All active subscriptions will be paused for the selected date range</li>
                  <li>Subscriptions will be extended automatically</li>
                  <li>Customers will be notified via SMS</li>
                </>
              ) : (
                <>
                  <li>All subscriptions will be unpaused from the selected date</li>
                  <li>Subscription end dates will be adjusted automatically</li>
                  <li>Customers will be notified via SMS</li>
                </>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          color={operationType === 'pause' ? 'warning' : 'success'}
          disabled={
            !selectedRestaurantId ||
            (operationType === 'pause' ? (!pauseStartDate || !pauseEndDate) : !unpauseFromDate)
          }
        >
          {operationType === 'pause' ? 'Confirm Bulk Pause' : 'Confirm Bulk Unpause'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// ----------------------------------------------------------------------

function SubscriptionCard({
  subscription,
  shouldShowRestaurantName,
  serverDate,
}: {
  subscription: components['schemas']['PartnersSubscriptionList'];
  shouldShowRestaurantName?: boolean;
  serverDate?: string;
}) {
  const { user } = useAuthContext();
  const canHandleMultipleRestaurants = hasPermissions(user, [
    'partners:misc:handle_multiple_restaurants',
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'unpaid':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'old':
        return 'default';
      default:
        return 'default';
    }
  };

  // Helper function to get pause status information
  const getPauseStatusInfo = () => {
    // Only process if subscription is active and has valid paused dates
    if (subscription.status !== 'active' || !subscription.has_valid_paused_dates || !serverDate) {
      return null;
    }

    const pausedDates = subscription.paused_dates || [];
    if (pausedDates.length === 0) {
      return null;
    }

    // Sort paused dates by date (ascending order)
    const sortedPausedDates = [...pausedDates].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const todayStr = serverDate;
    const today = new Date(serverDate);
    const subscriptionStart = new Date(subscription.start_date);

    // Filter paused dates that are relevant (>= subscription start date)
    const relevantPausedDates = sortedPausedDates.filter(pd => 
      new Date(pd.date) >= subscriptionStart
    );

    if (relevantPausedDates.length === 0) {
      return null;
    }

    // Check if today is a paused date AND subscription has started
    const todayPausedDate = relevantPausedDates.find(pd => pd.date === todayStr);
    
    if (todayPausedDate && today >= subscriptionStart) {
      // Find all paused dates from today onwards and get the latest one
      const todayAndFuturePausedDates = relevantPausedDates.filter(pd => pd.date >= todayStr);
      const latestPausedDate = todayAndFuturePausedDates[todayAndFuturePausedDates.length - 1].date;
      
      return {
        type: 'currently_paused',
        message: `Paused until ${formatDate(latestPausedDate)}`
      };
    }

    // Check for future paused dates (after today or after subscription start, whichever is later)
    const effectiveStartDate = today > subscriptionStart ? today : subscriptionStart;
    const futurePausedDates = relevantPausedDates.filter(pd => new Date(pd.date) > effectiveStartDate);
    
    if (futurePausedDates.length > 0) {
      // Show range from first to last future paused date
      const firstPausedDate = futurePausedDates[0].date;
      const lastPausedDate = futurePausedDates[futurePausedDates.length - 1].date;
      
      const startFormatted = formatDate(firstPausedDate);
      const endFormatted = formatDate(lastPausedDate);
      
      return {
        type: 'future_paused',
        message: startFormatted === endFormatted 
          ? `Will be Paused ${startFormatted}`
          : `Will be Paused from ${startFormatted} to ${endFormatted}`
      };
    }

    return null;
  };

  const formatDate = (dateString: string) => {
    // Parse the ISO date string and extract just the date part (YYYY-MM-DD)
    // This avoids timezone conversion issues
    const dateOnly = dateString.split('T')[0];
    const [, month, day] = dateOnly.split('-');
    
    // Format as DD/MM (British format)
    return `${day}/${month}`;
  };

  const formatPrice = (price: number) =>
    Number.isInteger(price) ? `${price}` : `${price.toFixed(3)}`;

  const navigate = useRouter();
  const restaurantId = subscription.restaurant.id;
  const pauseInfo = getPauseStatusInfo();

  return (
    <Card sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1, cursor: 'pointer' }} onClick={() => {
      navigate.push(paths.dashboard.restaurant.subscription.edit(restaurantId.toString(), subscription.id.toString()));
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" noWrap>
          {subscription.customer.user.phone_number}
        </Typography>
        <Stack direction="column" alignItems="flex-end" spacing={0.5}>
          
          {/* Always show the status chip */}
          <Label variant="outlined" color={getStatusColor(subscription.status)}>
            {subscription.status}
          </Label>
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="right" justifyContent="right">
        {/* Show pause status chip */}
        {pauseInfo && (
            <Label 
              variant="soft" 
              color="warning"
              sx={{ fontSize: '0.75rem' }}
            >
              {pauseInfo.message}
            </Label>
          )}
      </Stack>

      <Stack spacing={1}>
        {!shouldShowRestaurantName ? (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Plan
            </Typography>
            <Typography variant="body2">
              {subscription.restaurant_plan_group.special_name_en ||
                subscription.restaurant_plan_group.plan_tag.name_en}
            </Typography>
          </Stack>
        ) : (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Restaurant
            </Typography>
            <Typography variant="body2">{subscription.restaurant.name_en}</Typography>
          </Stack>
        )}
        {canHandleMultipleRestaurants && (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Commission
            </Typography>
            <Typography variant="body2">
              {subscription.transactions[0].commission_amount}
            </Typography>
          </Stack>
        )}

        {!canHandleMultipleRestaurants && (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Duration
            </Typography>
            <Typography variant="body2">
              {`${formatDate(subscription.start_date)} → ${formatDate(subscription.end_date)}`}
            </Typography>
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Created At
          </Typography>
          <Typography variant="body2">
            {subscription.created_at ? formatDate(subscription.created_at) : 'N/A'}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Price
          </Typography>
          <Typography variant="subtitle2" noWrap>
            {formatPrice(Number(subscription.price))}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
}

export default function RestaurantSubscriptionsListView({ id: restaurantId }: { id?: string }) {
  const { filters, onFilters, resetFilters } = useSubscriptionFilters();
  const { page, rowsPerPage, onPageChange, onRowsPerPageChange } = useSubscriptionPagination();

  const [tableData, setTableData] = useState<components['schemas']['PartnersSubscriptionList'][]>([]);

  // Get current server date for pause status calculations
  const { currentDateTime } = useGetCurrentDateTime();

  const { restaurantSubscriptions, restaurantSubscriptionsLoading } = useGetRestaurantSubscriptions(
    restaurantId,
    page + 1,
    rowsPerPage,
    filters.title,
    filters.status === 'all' ? '' : filters.status,
    undefined,  // start_date
    undefined,  // end_date
    filters.created_at_lte || undefined,
    filters.created_at_gte || undefined
  );

  useEffect(() => {
    if (restaurantSubscriptions?.results && !restaurantSubscriptionsLoading) {
      setTableData(restaurantSubscriptions.results);
    }
  }, [restaurantSubscriptions, restaurantSubscriptionsLoading]);

  const handlePageChange = useCallback(
    (_event: any, newPage: number) => {
      onPageChange(newPage);
    },
    [onPageChange]
  );

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      onRowsPerPageChange(newRowsPerPage);
    },
    [onRowsPerPageChange]
  );

  const notFound = !tableData.length;

  const { user } = useAuthContext();
  const canHandleMultipleRestaurants = hasPermissions(user, [
    'partners:misc:handle_multiple_restaurants',
  ]);

  const { enqueueSnackbar } = useSnackbar();

  const [showBulkModal, setShowBulkModal] = useState<boolean>(false);

  const handleBulkOperation = useCallback(() => {
    setShowBulkModal(true);
  }, []);

  const handleExport = useCallback(async () => {
    try {
      // Use the correct endpoint for Excel export
      const response = await client.GET('/partners/subscriptions-xlsx/', {
        params: {
          query: {
            title: filters.title || undefined,
            status: filters.status === 'all' ? undefined : filters.status,
            created_at_gte: filters.created_at_gte || undefined,
            created_at_lte: filters.created_at_lte || undefined,
          }
        },
        parseAs: 'blob'
      });

      if (response.error) {
        throw new Error('Export failed');
      }

      // Create download link with the blob data
      const blob = response.data as Blob;
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `subscriptions-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      enqueueSnackbar('Export successful', { variant: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar('Export failed', { variant: 'error' });
    }
  }, [filters, enqueueSnackbar]);

  return (
    <Container maxWidth={false}>
      <Grid container spacing={1} sx={{ mb: 3 }} alignItems="stretch">
        {/* Filters Card */}
        <Grid item xs={12} md={10}>
          <Card sx={{ height: '100%' }}>
            <SubscriptionTableToolbar
              filters={filters}
              onFilters={onFilters}
              onResetFilters={resetFilters}
            />
          </Card>
        </Grid>

        {/* Export Button Card */}
        <Grid item xs={12} md={2}>
          <Card sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 1, md: 1.5 }
          }}>
            <Stack 
              direction={{ xs: 'row', md: 'column' }} 
              spacing={{ xs: 1, md: 1 }}
              sx={{ width: '100%' }}
            >
              <Button
                variant="contained"
                startIcon={<Iconify icon="mdi:file-export" />}
                onClick={handleExport}
                fullWidth
                sx={{ 
                  minWidth: { xs: 'auto', md: '100%' },
                  flex: { xs: 1, md: 'none' }
                }}
              >
                Export/Excel
              </Button>
              <Button
                variant="contained"
                startIcon={<Iconify icon="mdi:pause" />}
                onClick={handleBulkOperation}
                fullWidth
                sx={{ 
                  minWidth: { xs: 'auto', md: '100%' },
                  flex: { xs: 1, md: 'none' }
                }}
              >
                Pause/Unpause
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {restaurantSubscriptionsLoading ? (
        <Grid container spacing={3}>
          {[...Array(rowsPerPage)].map((_, index) => (
            <Grid key={index} item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      width: '60%',
                      height: 20,
                      bgcolor: 'background.neutral',
                      borderRadius: 0.5,
                    }}
                  />
                  <Stack spacing={1}>
                    {[...Array(4)].map((__, i) => (
                      <Stack key={i} direction="row" justifyContent="space-between">
                        <Box
                          sx={{
                            width: '30%',
                            height: 20,
                            bgcolor: 'background.neutral',
                            borderRadius: 0.5,
                          }}
                        />
                        <Box
                          sx={{
                            width: '60%',
                            height: 20,
                            bgcolor: 'background.neutral',
                            borderRadius: 0.5,
                          }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          <Grid container spacing={3}>
            {tableData.map((subscription) => (
              <Grid key={subscription.id} item xs={12} sm={6} md={4}>
                <SubscriptionCard 
                  subscription={subscription} 
                  shouldShowRestaurantName={!restaurantId && canHandleMultipleRestaurants}
                  serverDate={currentDateTime?.date}
                />
              </Grid>
            ))}
          </Grid>

          {notFound && (
            <Box sx={{ py: 10 }}>
              <Typography variant="h6" align="center" sx={{ mb: 2 }}>
                No Subscriptions Found
              </Typography>
              <Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
                No results found for this search. Try adjusting your search criteria.
              </Typography>
            </Box>
          )}
        </>
      )}

      <Box sx={{ mt: 3 }}>
        <TablePaginationCustom
          count={restaurantSubscriptions?.count || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>

      {/* Bulk Operations Modal */}
      <BulkOperationsModal
        open={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        restaurantId={restaurantId}
        onSuccess={() => {
          // Refresh the subscriptions list after bulk operation
          window.location.reload();
        }}
      />
    </Container>
  );
}
